import { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";

import {
  Button,
  Grid2,
  MenuItem,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from "@mui/material";

import InvoicingComponent from "../components/InvoicingComponent";
import ContainerLoading from "../components/ContainerLoading";
import StudentRegister from "../components/StudentRegister";
import IndicatorsCard from "../components/IndicatorsCard";
import PageHeader from "../components/PageHeader";
import { Input } from "../components/Input";

import { reportFrequency, reportStudent } from "../services/reports";
import { classOptions } from "../services/class";
import StudentsIndicatorsList from "../components/StudentsIndicatorsList";
import AutomaticMessageAlertDialog from "../components/AutomaticMessageAlertDialog";
import { listStoreData } from "../services/dashboard";

const Reports = () => {
  const [open, setOpen] = useState(false);
  const [openPopup, setOpenPopup] = useState(false);
  const [indicatorsDetails, setIndicatorsDetails] = useState<{
    title: string;
    data: any;
    open: boolean;
  }>({
    open: false,
    data: null,
    title: "",
  });
  const [value, setValue] = useState("1");

  const [dates, setDates] = useState({
    startDate: moment().startOf("month").format("YYYY-MM-DD").toString(),
    endDate: moment().endOf("month").format("YYYY-MM-DD").toString(),
    fightsId: "",
  });

  const [filters, setFilters] = useState({
    startDate: moment().startOf("month").toDate(),
    endDate: moment().endOf("month").toDate(),
  });

  const handleChangeValue = (
    _event: React.SyntheticEvent,
    newValue: string
  ) => {
    setValue(newValue);
  };

  const {
    data: dataStore,
    isLoading: isLoadingStore,
    isFetching: isFetchingStore,
    refetch: refetchStore,
  }: {
    data: any;
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  } = useQuery({
    queryFn: listStoreData,
    queryKey: ["listStoreData"],
    initialData: { whatsappPopupDismissed: true, autoNotificationWpp: true },
  });

  const {
    data,
    isLoading,
    isFetching,
  }: {
    data: {
      studentCheckinAlmostDay: number;
      studentCheckinAlmostDayList: [];
      studentCheckinLastDay: number;
      studentCheckinLastDayList: [];
      studentsActive: number;
      studentsActiveList: [];
      studentsBuyMarkerplace: number;
      studentsBuyMarkerplaceList: [];
      studentsPaymentsOnDay: number;
      studentsPaymentsOnDayList: [];
      studentsPaymentsOverdue: number;
      studentsPaymentsOverdueList: [];
    };
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  } = useQuery({
    queryKey: ["reportStudent"],
    queryFn: async () => await reportStudent({}),
    initialData: {
      studentCheckinAlmostDay: 0,
      studentCheckinAlmostDayList: [],
      studentCheckinLastDay: 0,
      studentCheckinLastDayList: [],
      studentsActive: 0,
      studentsActiveList: [],
      studentsBuyMarkerplace: 0,
      studentsBuyMarkerplaceList: [],
      studentsPaymentsOnDay: 0,
      studentsPaymentsOnDayList: [],
      studentsPaymentsOverdue: 0,
      studentsPaymentsOverdueList: [],
    },
  });

  const {
    data: dataFrequency,
    isLoading: isLoadingFrequency,
    isFetching: isFetchingFrequency,
  }: {
    data: {
      frequency: { name: string; checkin_count: number }[];
      frequencyAmount: number;
      frequencyByStudent: { fullName: string; checkin_count: number }[];
    };
    isLoading: boolean;
    isFetching: boolean;
    refetch: () => void;
  } = useQuery({
    queryKey: ["reportFrequency" + JSON.stringify(filters)],
    queryFn: async () =>
      await reportFrequency({
        ...filters,
      }),
    initialData: {
      frequency: [{ name: "", checkin_count: 0 }],
      frequencyAmount: 0,
      frequencyByStudent: [{ fullName: "", checkin_count: 0 }],
    },
  });

  const {
    data: dataOptions,
    isLoading: isLoadingOptions,
    isFetching: isFetchingOptions,
  }: {
    data: {
      professor: { id: string; fullName: string }[];
      spaces: [{ id: string; name: string }];
      fights: [{ id: string; name: string }];
    };
    isLoading: boolean;
    isFetching: boolean;
  } = useQuery({
    queryFn: classOptions,
    queryKey: ["classOptions"],
    initialData: {
      professor: [],
      spaces: [],
      fights: [],
    },
  });

  useEffect(() => {
    if (dataStore) {
      const shouldOpenPopup =
        !dataStore.whatsappPopupDismissed &&
        !dataStore.autoNotificationWpp &&
        moment(dataStore.whatsappPopupUpdatedAt).isSameOrBefore(
          moment(),
          "date"
        );

      setOpenPopup(shouldOpenPopup);
    }
  }, [dataStore]);

  const refetchAll = () => {};

  const loading =
    isLoading ||
    isFetching ||
    isLoadingFrequency ||
    isFetchingFrequency ||
    isLoadingOptions ||
    isFetchingOptions ||
    isLoadingStore ||
    isFetchingStore;

  return (
    <ContainerLoading loading={loading}>
      <Grid2 container spacing={2} padding={2}>
        <PageHeader
          title="Início"
          subtitle="Informações gerais dos relatórios."
        />
        <Grid2 size={12}>
          <Tabs
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              marginBottom: 2,
            }}
            onChange={handleChangeValue}
            value={value}
            textColor="inherit"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Alunos" value="1" />
            <Tab label="Frequência" value="2" />
            <Tab label="Financeiro" value="3" />
          </Tabs>
        </Grid2>
        {value === "1" && (
          <Grid2 container spacing={2}>
            <Grid2 size={12}>
              <IndicatorsCard
                data={[
                  {
                    id: 1,
                    title: "Ativos",
                    value: data?.studentsActive,
                    icon: "icon_school",
                    size: { xs: 12, sm: 6, lg: 4 },
                    titleButton: "Visualizar",
                    onButton: () =>
                      setIndicatorsDetails({
                        title: "Ativos",
                        data: data?.studentsActiveList,
                        open: true,
                      }),
                  },
                  {
                    id: 2,
                    title: "Pagamentos atrasados",
                    value: data?.studentsPaymentsOverdue,
                    icon: "icon_school",
                    size: { xs: 12, sm: 6, lg: 4 },
                    titleButton: "Visualizar",
                    onButton: () =>
                      setIndicatorsDetails({
                        title: "Pagamentos atrasados",
                        data: data?.studentsPaymentsOverdueList,
                        open: true,
                      }),
                  },
                  {
                    id: 3,
                    title: "Pagamentos em dia",
                    value: data?.studentsPaymentsOnDay,
                    icon: "icon_school",
                    size: { xs: 12, sm: 6, lg: 4 },
                    titleButton: "Visualizar",
                    onButton: () =>
                      setIndicatorsDetails({
                        title: "Pagamentos em dia",
                        data: data?.studentsPaymentsOnDayList,
                        open: true,
                      }),
                  },
                  {
                    id: 1,
                    title: "Check-ins dos últimos 15 dias",
                    value: data?.studentCheckinLastDay,
                    icon: "icon_school",
                    size: { xs: 12, sm: 6, lg: 4 },
                    titleButton: "Visualizar",
                    onButton: () =>
                      setIndicatorsDetails({
                        title: "Check-ins dos últimos 15 dias",
                        data: data?.studentCheckinLastDayList,
                        open: true,
                      }),
                  },

                  {
                    id: 1,
                    title: "Sem check-ins nos últimos 15 dias",
                    value: data?.studentCheckinAlmostDay,
                    icon: "icon_school",
                    size: { xs: 12, sm: 6, lg: 4 },
                    titleButton: "Visualizar",
                    onButton: () =>
                      setIndicatorsDetails({
                        title: "Sem check-ins nos últimos 15 dias",
                        data: data?.studentCheckinAlmostDayList,
                        open: true,
                      }),
                  },
                  {
                    id: 3,
                    title: "Compras realizadas na loja",
                    value: data?.studentsBuyMarkerplace,
                    icon: "icon_school",
                    size: { xs: 12, sm: 6, lg: 4 },
                    titleButton: "Visualizar",
                    onButton: () =>
                      setIndicatorsDetails({
                        title: "Compras realizadas na loja",
                        data: data?.studentsBuyMarkerplaceList,
                        open: true,
                      }),
                  },
                ]}
              />
            </Grid2>
          </Grid2>
        )}
        {value === "2" && (
          <Grid2 size={12}>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, lg: 4 }}>
                <Stack
                  sx={{
                    padding: 2,
                    borderRadius: 2,
                    backgroundColor: "primary.main",
                    textAlign: "center",
                  }}
                >
                  <Typography variant="body1" fontSize={36} fontWeight={700}>
                    {dataFrequency.frequencyAmount}
                  </Typography>
                  <Typography variant="h3" fontSize={14} fontWeight={600}>
                    Total de frequências
                  </Typography>
                </Stack>
              </Grid2>
              <Grid2 size={{ xs: 12, lg: 8 }}>
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  sx={{
                    justifyContent: "space-between",
                    alignItems: "cneter",
                    gap: 2,
                    padding: 2,
                    borderRadius: 2,
                    backgroundColor: "secondary.dark",
                  }}
                >
                  <Input
                    id="startDate"
                    name="startDate"
                    label="Data início"
                    type="date"
                    fullWidth
                    size="small"
                    value={moment(dates.startDate).format("YYYY-MM-DD")}
                    onChange={(e: { target: { value: string } }) =>
                      setDates({ ...dates, startDate: e.target.value })
                    }
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                      e.target.showPicker()
                    }
                  />
                  <Input
                    id="endDate"
                    name="endDate"
                    type="date"
                    label="Data fim"
                    fullWidth
                    size="small"
                    value={moment(dates.endDate).format("YYYY-MM-DD")}
                    onChange={(e: { target: { value: string } }) =>
                      setDates({ ...dates, endDate: e.target.value })
                    }
                    onFocus={(e: React.FocusEvent<HTMLInputElement>) =>
                      e.target.showPicker()
                    }
                  />
                  <Stack sx={{ minWidth: 150 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: 12 }}>
                      Modalidades
                    </Typography>
                    <Select
                      sx={{ marginTop: 1 }}
                      id="fightId"
                      value={dates.fightsId}
                      fullWidth
                    >
                      {dataOptions?.fights?.map((item) => (
                        <MenuItem
                          key={item?.id}
                          value={item?.id}
                          onClick={() =>
                            setDates({ ...dates, fightsId: item?.id })
                          }
                        >
                          {item?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                  <Stack sx={{ justifyContent: "center", mt: 3 }}>
                    <Button
                      size="small"
                      variant="contained"
                      onClick={() => setFilters(dates as any)}
                    >
                      Filtrar
                    </Button>
                  </Stack>
                </Stack>
              </Grid2>
            </Grid2>
          </Grid2>
        )}
        {value === "2" && (
          <Grid2 size={12}>
            <Grid2 container spacing={2}>
              <Grid2 size={{ xs: 12, lg: 6 }}>
                <Typography variant="h2" fontWeight={600} fontSize={16} mb={1}>
                  Frequência por modalidade
                </Typography>
                <TableContainer sx={{ height: "auto", borderRadius: 2 }}>
                  <Table
                    sx={{
                      minWidth: 300,
                      backgroundColor: "secondary.100",
                      borderRadius: 2,
                    }}
                    aria-label="simple table"
                  >
                    <TableHead sx={{ backgroundColor: "secondary.dark" }}>
                      <TableRow>
                        <TableCell align="center" sx={styles.ceil}>
                          Modalidade
                        </TableCell>
                        <TableCell align="center" sx={styles.ceil}>
                          Frequência
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    {dataFrequency.frequency.length !== 0 ? (
                      <TableBody>
                        {dataFrequency.frequency.map((item, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell
                              align="center"
                              component="th"
                              scope="row"
                              sx={styles.ceil}
                            >
                              {item.name}
                            </TableCell>
                            <TableCell
                              align="center"
                              component="th"
                              scope="row"
                              sx={styles.ceil}
                            >
                              {item.checkin_count}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    ) : (
                      <TableBody>
                        <TableRow
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell
                            align="center"
                            component="th"
                            scope="row"
                            sx={styles.ceil}
                          >
                            Sem resultados
                          </TableCell>
                          <TableCell
                            align="center"
                            component="th"
                            scope="row"
                            sx={styles.ceil}
                          >
                            0
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Grid2>
              <Grid2 size={{ xs: 12, lg: 6 }}>
                <Typography variant="h2" fontWeight={600} fontSize={16} mb={1}>
                  Frequência por alunos
                </Typography>
                <TableContainer sx={{ height: "auto", borderRadius: 2 }}>
                  <Table
                    sx={{
                      minWidth: 300,
                      backgroundColor: "secondary.100",
                      borderRadius: 2,
                    }}
                    aria-label="simple table"
                  >
                    <TableHead sx={{ backgroundColor: "secondary.dark" }}>
                      <TableRow>
                        <TableCell align="center" sx={styles.ceil}>
                          Aluno
                        </TableCell>
                        <TableCell align="center" sx={styles.ceil}>
                          Frequência
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    {dataFrequency.frequencyByStudent.length !== 0 ? (
                      <TableBody>
                        {dataFrequency.frequencyByStudent.map((item, index) => (
                          <TableRow
                            key={index}
                            sx={{
                              "&:last-child td, &:last-child th": { border: 0 },
                            }}
                          >
                            <TableCell
                              align="center"
                              component="th"
                              scope="row"
                              sx={styles.ceil}
                            >
                              {item.fullName}
                            </TableCell>
                            <TableCell
                              align="center"
                              component="th"
                              scope="row"
                              sx={styles.ceil}
                            >
                              {item.checkin_count}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    ) : (
                      <TableBody>
                        <TableRow
                          sx={{
                            "&:last-child td, &:last-child th": { border: 0 },
                          }}
                        >
                          <TableCell
                            align="center"
                            component="th"
                            scope="row"
                            sx={styles.ceil}
                          >
                            Sem resultados
                          </TableCell>
                          <TableCell
                            align="center"
                            component="th"
                            scope="row"
                            sx={styles.ceil}
                          >
                            0
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    )}
                  </Table>
                </TableContainer>
              </Grid2>
            </Grid2>
          </Grid2>
        )}
        {value === "3" && (
          <Grid2 size={12} sx={styles.cardStyle}>
            <InvoicingComponent />
          </Grid2>
        )}
      </Grid2>
      <StudentRegister
        open={open}
        onClose={() => setOpen(false)}
        refetch={() => refetchAll()}
      />
      <StudentsIndicatorsList
        data={indicatorsDetails?.data}
        title={indicatorsDetails?.title}
        open={indicatorsDetails?.open}
        onClose={() =>
          setIndicatorsDetails({ title: "", data: null, open: false })
        }
        refetch={() => refetchAll()}
      />
      <AutomaticMessageAlertDialog
        open={openPopup}
        onClose={() => setOpenPopup(false)}
        refetch={() => refetchStore()}
      />
      {/* <StudentFilters
        open={openFilters}
        onClose={() => setOpenFilters(false)}
        onFilter={onFilter}
      /> */}
    </ContainerLoading>
  );
};

const styles = {
  ceil: { padding: "0.5rem 1rem", borderColor: "secondary.main" },
  cardStyle: {
    backgroundColor: "secondary.main",
    borderRadius: 2,
    padding: 2,
  },
};

export default Reports;
